import { Phone, Menu, X } from "lucide-react";
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import "./Header.css";

const PHONE_NUMBER = "9289440048";
const PHONE_DISPLAY = "9289 44 00 48";
const WHATSAPP_URL = `https://wa.me/91${PHONE_NUMBER}`;
const TEL_URL = `tel:+91${PHONE_NUMBER}`;

function WhatsAppIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="#25D366">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
      <path d="M12 0C5.373 0 0 5.373 0 12c0 2.117.554 4.103 1.523 5.824L.057 23.882a.5.5 0 0 0 .613.613l6.058-1.466A11.94 11.94 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.802 9.802 0 0 1-5.003-1.374l-.358-.214-3.714.899.916-3.634-.234-.374A9.818 9.818 0 0 1 2.182 12C2.182 6.57 6.57 2.182 12 2.182S21.818 6.57 21.818 12 17.43 21.818 12 21.818z" />
    </svg>
  );
}

const navItems = ["Home", "About Us", "Pricing Plan", "Our Policies", "FAQs"];

export function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  return (
    <>
      <header className="header">
        {/* Top bar with WhatsApp and phone - full width */}
        <div className="header-topbar">
          <div className="header-topbar-contacts">
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="topbar-contact-link"
            >
              <WhatsAppIcon />
              <span>{PHONE_DISPLAY}</span>
            </a>
            <span className="topbar-separator">|</span>
            <a href={TEL_URL} className="topbar-contact-link topbar-phone-link">
              <Phone style={{ width: 16, height: 16 }} />
              <span>{PHONE_DISPLAY}</span>
            </a>
          </div>
        </div>

        <div className="header-inner">
          {/* Main header */}
          <div className="header-main">
            {/* Logo */}
            <Link to="/" className="header-logo">
              <img src="/images/docmasterLogo.png" alt="Docmaster" />
            </Link>

            {/* Desktop Navigation */}
            <nav className="header-nav-desktop">
              {navItems.map((item) =>
                item === "Home" ? (
                  <Link key={item} to="/" className="header-nav-link">
                    {item}
                  </Link>
                ) : (
                  <a key={item} href="#" className="header-nav-link">
                    {item}
                  </a>
                ),
              )}
            </nav>

            {/* Desktop Auth buttons */}
            <div className="header-auth-desktop">
              <button
                className="header-btn-login"
                onClick={() => navigate("/login")}
              >
                Login
              </button>
              <button
                className="header-btn-signup"
                onClick={() => navigate("/signup")}
              >
                Sign Up
              </button>
            </div>

            {/* Mobile hamburger */}
            <button
              className="header-hamburger"
              onClick={() => setMenuOpen(true)}
              aria-label="Open menu"
            >
              <Menu style={{ width: 28, height: 28 }} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile overlay */}
      <div
        className={`sidebar-overlay ${menuOpen ? "sidebar-overlay-visible" : ""}`}
        onClick={() => setMenuOpen(false)}
      />

      {/* Mobile glassmorphism side nav */}
      <aside className={`sidebar ${menuOpen ? "sidebar-open" : ""}`}>
        <div className="sidebar-header">
          <Link to="/" onClick={() => setMenuOpen(false)}>
            <img
              src="/images/docmasterLogo.png"
              alt="Docmaster"
              className="sidebar-logo"
            />
          </Link>
          <button
            className="sidebar-close"
            onClick={() => setMenuOpen(false)}
            aria-label="Close menu"
          >
            <X style={{ width: 24, height: 24 }} />
          </button>
        </div>

        <nav className="sidebar-nav">
          {navItems.map((item) =>
            item === "Home" ? (
              <Link
                key={item}
                to="/"
                className="sidebar-nav-link"
                onClick={() => setMenuOpen(false)}
              >
                {item}
              </Link>
            ) : (
              <a
                key={item}
                href="#"
                className="sidebar-nav-link"
                onClick={() => setMenuOpen(false)}
              >
                {item}
              </a>
            ),
          )}
        </nav>

        <div className="sidebar-auth">
          <button
            className="sidebar-btn-login"
            onClick={() => {
              setMenuOpen(false);
              navigate("/login");
            }}
          >
            Login
          </button>
          <button
            className="sidebar-btn-signup"
            onClick={() => {
              setMenuOpen(false);
              navigate("/signup");
            }}
          >
            Sign Up
          </button>
        </div>

        <div className="sidebar-phone">
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="sidebar-contact-link"
          >
            <WhatsAppIcon />
            <span>{PHONE_DISPLAY}</span>
          </a>
          <span className="topbar-separator">|</span>
          <a href={TEL_URL} className="sidebar-contact-link">
            <Phone style={{ width: 16, height: 16 }} />
            <span>{PHONE_DISPLAY}</span>
          </a>
        </div>
      </aside>
    </>
  );
}
